import { VStack } from "components/Stack";
import { UserContext } from "contexts/UserContext";
import { useRequiredContext } from "contexts/utils";
import useAccountForm from "../hook";
import Button from "components/ui/Button";
import PasswordRequirement from "components/PasswordRequirement";
import { useMemo, useState } from "react";
import Spinner from "components/ui/Spinner";
import { logIn, verifyUser } from "services/users/auth";
import PasswordInput from "components/ui/PasswordInput";
import { Trans, useTranslation } from "react-i18next";
import { WEBSITE_ROUTES } from "pages/routes";
import ApiErrorBanner from "components/ApiErrorBanner";

type Props = {
  onNextStep: () => void;
  location: { [key: string]: any };
};

const initData = {
  password: "",
  confirmPassword: "",
};

function CreateAccount(props: Props) {
  const { refreshCurrentUser } = useRequiredContext(UserContext);
  const {
    account,
    setAccount,
    errors,
    touched,
    onTouched,
    setTouched,
    passwordStatus,
    onValidate,
  } = useAccountForm(initData);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<any>("");
  const { t } = useTranslation();

  const isValidForm = useMemo(
    () => onValidate(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account.password, account.confirmPassword]
  );

  const hdChange = (event: any, name: string) => {
    let value = event.target?.value;
    setAccount(prev => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e: any) => {
    onTouched(e.target?.name);
  };

  const onError = (error: any) => {
    setErr(error);
    setLoading(false);
  };

  const hdSubmit = () => {
    setTouched({
      password: true,
      confirmPassword: true,
    });

    const isValidated = onValidate();
    if (isValidated) {
      setLoading(true);
      setErr(null);

      const parseData = new URLSearchParams(props.location?.search || "");

      verifyUser({ token: parseData.get("token")!, passwd: account.password })
        .then(async rs => {
          if (!account.password || !rs.email) {
            return;
          }

          await logIn({
            email: rs.email,
            passwd: account.password,
          });
          await refreshCurrentUser();

          setAccount({ password: "", confirmPassword: "" });
          props.onNextStep();
          setLoading(false);
        })
        .catch(onError);
    }
  };

  return (
    <div className={"create-account-step"}>
      <h1 className={"step-title"}>{t("Auth.invitation.createAccountTitle")}</h1>

      <div className={"content"}>
        <div>
          <ApiErrorBanner error={err} className="create-account-error" />

          <VStack gap={24}>
            <PasswordInput
              label={t("Auth.emailSignup.passwordLabel")}
              error={touched.password && errors.password}
              size="large"
              className="full-width"
              name="password"
              value={account.password}
              onChange={(e: any) => hdChange(e, "password")}
              invalid={!!(errors.password && touched.password)}
              onBlur={handleBlur}
            />

            <PasswordInput
              label={t("Auth.emailSignup.confirmPasswordLabel")}
              error={touched.confirmPassword && errors.confirmPassword}
              size="large"
              className="full-width"
              name="confirmPassword"
              value={account.confirmPassword}
              onChange={(e: any) => hdChange(e, "confirmPassword")}
              invalid={!!(errors.confirmPassword && touched.confirmPassword)}
              onBlur={handleBlur}
            />
          </VStack>

          {account.password && <PasswordRequirement passwordStatus={passwordStatus} />}

          <div className="legal-panel">
            <p>
              <Trans
                i18nKey={"Auth.signUpPage.legalNotice"}
                components={{
                  privacy: (
                    <a
                      href={WEBSITE_ROUTES.privacyPolicy}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                  terms: (
                    <a
                      href={WEBSITE_ROUTES.termsOfService}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
            </p>
          </div>

          {loading && <Spinner />}

          {!loading && (
            <Button disabled={!isValidForm} className="step-action" onClick={hdSubmit}>
              {t("Auth.emailSignup.submit")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateAccount;
